import { BookOpenIcon, StickyNoteIcon, SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { ComboBox, ListBox, ListBoxItem } from "react-aria-components";
import { useAsyncList } from "react-stately";

import { Spinner } from "@/components/Spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SearchInput } from "@/components/ui/search-input";
import { DocSearchResult, searchContent } from "@/lib/api";
import { cn } from "@/lib/utils";

function EmptyState() {
  return (
    <div className="py-4 pb-8 text-slate-dim text-sm flex items-center justify-center">
      <p>Search for docs, guides, and more</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="py-4 pb-8 text-slate-dim text-sm flex items-center justify-center">
      <Spinner />
    </div>
  );
}

export function ContentSearch({
  variant,
}: {
  variant: "teal" | "light" | "orange" | "slate";
}) {
  const scrollDetectorRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsSticky(!entry.isIntersecting);
      });
    });

    if (scrollDetectorRef.current) {
      observer.observe(scrollDetectorRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const list = useAsyncList<DocSearchResult>({
    async load({ filterText }) {
      if (!filterText) return { items: [] };

      return {
        items: await searchContent(filterText),
      };
    },
  });

  return (
    <div className="md:flex-1">
      <Dialog>
        <DialogTrigger
          aria-label="Search"
          className={cn(
            "group",
            "flex items-center justify-center gap-2 rounded-lg h-10 w-10 md:w-auto md:px-3",
            variant === "teal" && "bg-slate-action text-slate-dim",
            variant === "light" &&
              "bg-white/70 hover:bg-white/90 text-slate-11 hover:text-slate-12",
            variant === "orange" && "bg-orange-action text-orange-dim",
            variant === "slate" && "bg-slate-action text-slate-dim",
          )}
        >
          <SearchIcon className="w-4 h-4" />
          <div className="text-sm hidden md:group-hover:flex">Search</div>
        </DialogTrigger>
        <DialogContent closeButton={false} className="p-0 gap-0">
          <VisuallyHidden>
            <DialogTitle>Search content</DialogTitle>
            <DialogDescription>
              Find documents, guides, and more
            </DialogDescription>
          </VisuallyHidden>
          <ComboBox
            inputValue={list.filterText}
            onInputChange={(v) => list.setFilterText(v)}
            items={list.items}
            allowsCustomValue={true}
            allowsEmptyCollection={true}
            onSelectionChange={(key) => {
              if (!key) return;
              const item = list.getItem(key);
              if (!item) return;
              window.location.href = item.document.url;
            }}
          >
            <div
              className={cn(
                "sticky top-0 bg-slate-app p-4",
                isSticky && "shadow-md",
              )}
            >
              <SearchInput wrapperAsDiv aria-label="Search content" />
            </div>
            <div ref={scrollDetectorRef} />
            <ListBox
              className="flex flex-col pb-2 px-3"
              renderEmptyState={() => {
                if (list.isLoading) {
                  return <LoadingState />;
                }

                return <EmptyState />;
              }}
            >
              {(result: DocSearchResult) => {
                return (
                  <ListBoxItem
                    key={result.id}
                    textValue={result.document.title}
                    className="flex items-center gap-4 bg-slate-ghost px-3 rounded-lg data-[focus-visible='true']:bg-slate-action my-0.5"
                  >
                    <div className="text-slate-dim">
                      {result.document.type === "post" ? (
                        <StickyNoteIcon className="w-5 h-5" />
                      ) : (
                        <BookOpenIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div className="py-2">
                      <h3 className="font-semibold">{result.document.title}</h3>
                      <p className="text-sm text-slate-dim">
                        {result.document.description}
                      </p>
                    </div>
                  </ListBoxItem>
                );
              }}
            </ListBox>
          </ComboBox>
        </DialogContent>
      </Dialog>
    </div>
  );
}
